const kanoRoles = [
  'Community programs & champion networks',
  'Provider training & capacity building',
  'Family support groups',
  'Local Hausa-language media campaigns',
  'Task-shifted epilepsy access pilot clinics',
  'Proof point for scalable service delivery',
];

const abujaRoles = [
  'Policy advocacy & national coalition building',
  'Donor relations & federal partnerships',
  'National media & digital coordination hub',
  'Federal health reform engagement',
  'Patient ambassador & case study generation',
  'National epilepsy report platform',
];

export default function GeographicStrategy() {
  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-4">
          <span className="text-xs font-semibold uppercase tracking-[4px] text-[#7B2D8B]">Geographic Logic</span>
        </div>
        <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 text-center mb-4">
          Two Cities, <span className="text-[#7B2D8B]">One Strategy</span>
        </h2>
        <p className="text-gray-500 text-center max-w-2xl mx-auto mb-16 leading-relaxed">
          Kano as the implementation stronghold. Abuja as the policy and national partnerships platform. Together, they give IKIRU credibility in the North and visibility at the national level.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Kano */}
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Kano%20Nigeria%20traditional%20city%20view%20with%20ancient%20walls%20and%20minarets%2C%20warm%20golden%20sunset%20light%2C%20historic%20Northern%20Nigerian%20architecture%2C%20vibrant%20and%20culturally%20rich%20atmosphere%2C%20wide%20landscape%20photography&width=800&height=500&seq=geo01&orientation=landscape"
              alt="Kano - Implementation Hub"
              className="w-full h-64 object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2D0A35]/90 via-[#2D0A35]/30 to-transparent"></div>
            <div className="absolute top-5 left-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#7B2D8B] text-white text-xs font-bold uppercase tracking-widest">
                <i className="ri-map-pin-fill text-xs"></i>
                Kano
              </span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <p className="text-purple-200 text-xs font-semibold uppercase tracking-widest mb-2">Implementation Stronghold</p>
              <p className="text-white/80 text-sm leading-relaxed">
                Dense urban population, strong Hausa-language reach, and alignment with existing clinical and research networks at AKTH and BUK.
              </p>
            </div>
          </div>

          {/* Abuja */}
          <div className="relative rounded-3xl overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Abuja%20Nigeria%20modern%20city%20skyline%20with%20government%20buildings%20and%20wide%20boulevards%2C%20clear%20blue%20sky%2C%20contemporary%20African%20capital%20city%2C%20professional%20and%20authoritative%20atmosphere%2C%20wide%20landscape%20photography&width=800&height=500&seq=geo02&orientation=landscape"
              alt="Abuja - Policy Platform"
              className="w-full h-64 object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A0A1E]/90 via-[#1A0A1E]/30 to-transparent"></div>
            <div className="absolute top-5 left-5">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#9333EA] text-white text-xs font-bold uppercase tracking-widest">
                <i className="ri-map-pin-fill text-xs"></i>
                Abuja
              </span>
            </div>
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <p className="text-purple-200 text-xs font-semibold uppercase tracking-widest mb-2">Policy &amp; National Platform</p>
              <p className="text-white/80 text-sm leading-relaxed">
                Visibility with federal institutions, development partners, corporate donors, and the media — the natural centre for national advocacy.
              </p>
            </div>
          </div>
        </div>

        {/* Role breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-[#FAF7FC] rounded-2xl p-8 border border-purple-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#7B2D8B]">
                <i className="ri-community-line text-white text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Kano&apos;s Role</h3>
                <p className="text-gray-500 text-xs">Community programs &amp; service delivery</p>
              </div>
            </div>
            <ul className="space-y-3">
              {kanoRoles.map((role) => (
                <li key={role} className="flex items-start gap-3 text-sm text-gray-700">
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#7B2D8B]/15 shrink-0 mt-0.5">
                    <i className="ri-check-line text-[#7B2D8B] text-xs"></i>
                  </span>
                  {role}
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-[#FAF7FC] rounded-2xl p-8 border border-purple-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#9333EA]">
                <i className="ri-government-line text-white text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Abuja&apos;s Role</h3>
                <p className="text-gray-500 text-xs">Policy advocacy &amp; national partnerships</p>
              </div>
            </div>
            <ul className="space-y-3">
              {abujaRoles.map((role) => (
                <li key={role} className="flex items-start gap-3 text-sm text-gray-700">
                  <span className="w-5 h-5 flex items-center justify-center rounded-full bg-[#9333EA]/15 shrink-0 mt-0.5">
                    <i className="ri-check-line text-[#9333EA] text-xs"></i>
                  </span>
                  {role}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Expansion note */}
        <div className="mt-8 bg-gradient-to-r from-[#7B2D8B]/10 to-[#9333EA]/10 rounded-2xl p-6 border border-purple-200 flex flex-col md:flex-row items-start md:items-center gap-4">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#7B2D8B] shrink-0">
            <i className="ri-arrow-right-up-line text-white text-lg"></i>
          </div>
          <div>
            <p className="font-semibold text-gray-900 text-sm mb-1">Wider Northern Nigeria Expansion</p>
            <p className="text-gray-500 text-sm leading-relaxed">
              Gradual expansion to nearby states through partnerships and replication — allowing Kano-tested models to scale regionally with cultural adaptation, eventually positioning IKIRU as the leading epilepsy voice in Nigeria.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
